import { Helmet } from 'react-helmet-async';
import { BASE_URL } from '@/lib/constants';

interface UnifiedSEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  canonical?: string;
  noindex?: boolean;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
  faq?: Array<{
    question: string;
    answer: string;
  }>;
  localBusiness?: {
    name: string;
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
    phone: string;
    email: string;
  };
  schema?: {
    type: string;
    name: string;
    description: string;
    provider?: string;
    foundingDate?: string;
    founder?: string;
  };
}

const UnifiedSEO = ({ 
  title = "SeniorThrive - Empowering Older Adults to Thrive at Home",
  description = "The all-in-one platform that empowers older adults, families, and caregivers to prevent falls, boost wellness, and stay connected—right from home. Start your 7-day free trial.",
  keywords,
  image = `${BASE_URL}/opengraph-image.png`,
  url,
  type = "website",
  canonical,
  noindex = false,
  article,
  breadcrumbs,
  faq,
  localBusiness,
  schema
}: UnifiedSEOProps) => {
  // Optimize title length (max 60 characters for better SERP display)
  const optimizedTitle = title.length > 60 ? title.substring(0, 57) + '...' : title;
  const fullTitle = optimizedTitle.includes('SeniorThrive') ? optimizedTitle : `${optimizedTitle} | SeniorThrive`;
  
  // Optimize description length (150-160 characters for better SERP display)
  const optimizedDescription = description.length > 160 ? description.substring(0, 157) + '...' : description;
  
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : BASE_URL);
  const canonicalUrl = canonical || currentUrl;
  
  // Enhanced Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SeniorThrive",
    "alternateName": "Senior Thrive",
    "url": BASE_URL,
    "description": "SeniorThrive empowers older adults to live safely and independently at home with smart fall prevention, health tracking, and family coordination tools.",
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/logo.png`,
      "width": 400,
      "height": 100
    },
    "foundingDate": "2024",
    "slogan": "Empowering Older Adults to Thrive at Home",
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+1-800-THRIVE-1",
        "contactType": "customer service",
        "email": "support@seniorthrive.com",
        "availableLanguage": ["English"],
        "areaServed": "US"
      }
    ],
    "sameAs": [
      "https://twitter.com/SeniorThrive",
      "https://linkedin.com/company/seniorthrive",
      "https://facebook.com/SeniorThrive"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "247",
      "bestRating": "5"
    },
    "knowsAbout": [
      "Senior Care",
      "Home Safety",
      "Fall Prevention",
      "Aging in Place",
      "Family Caregiving",
      "Health Monitoring"
    ]
  };

  // Enhanced Website Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SeniorThrive",
    "alternateName": "Senior Thrive",
    "url": BASE_URL,
    "description": optimizedDescription,
    "publisher": {
      "@type": "Organization",
      "@id": `${BASE_URL}/#organization`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${BASE_URL}/support?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  // Enhanced Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "SeniorThrive Platform",
    "description": "Comprehensive platform for senior safety, wellness tracking, and family coordination to help older adults thrive at home.",
    "provider": {
      "@type": "Organization",
      "@id": `${BASE_URL}/#organization`
    },
    "serviceType": "Senior Care Technology Platform",
    "category": "Healthcare Technology",
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "audience": [
      {
        "@type": "Audience",
        "name": "Older Adults",
        "audienceType": "Healthcare Consumer"
      },
      {
        "@type": "Audience", 
        "name": "Family Caregivers",
        "audienceType": "Healthcare Consumer"
      },
      {
        "@type": "Audience",
        "name": "Professional Caregivers",
        "audienceType": "Healthcare Professional"
      }
    ],
    "offers": {
      "@type": "Offer",
      "name": "SeniorThrive Free Trial",
      "description": "7-day free trial of complete platform features",
      "price": "0",
      "priceCurrency": "USD",
      "validFrom": "2024-01-01",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "@id": `${BASE_URL}/#organization`
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "SeniorThrive Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Smart Home Safety Scans",
            "description": "Advanced analysis to identify fall risks and safety hazards"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Family Care Coordination",
            "description": "ThriveCircle platform for coordinating family caregiving"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Wellness Tracking & ThriveScore",
            "description": "Comprehensive health and wellness monitoring with personalized scoring"
          }
        }
      ]
    }
  };

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  } : null;

  // FAQ Schema
  const faqSchema = faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Local Business Schema
  const localBusinessSchema = localBusiness ? {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": localBusiness.name,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": localBusiness.address.street,
      "addressLocality": localBusiness.address.city,
      "addressRegion": localBusiness.address.state,
      "postalCode": localBusiness.address.postalCode,
      "addressCountry": localBusiness.address.country
    },
    "telephone": localBusiness.phone,
    "email": localBusiness.email,
    "url": canonicalUrl
  } : null;

  // Custom Schema
  const customSchema = schema ? {
    "@context": "https://schema.org",
    "@type": schema.type,
    "name": schema.name,
    "description": schema.description,
    ...(schema.provider && { "provider": { "@type": "Organization", "name": schema.provider } }),
    ...(schema.foundingDate && { "foundingDate": schema.foundingDate }),
    ...(schema.founder && { "founder": schema.founder })
  } : null;

  return (
    <Helmet>
      {/* Primary SEO Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={optimizedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots Directives */}
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}
      
      {/* Enhanced Meta Tags */}
      <meta name="author" content="SeniorThrive" />
      <meta name="publisher" content="SeniorThrive" />
      <meta name="copyright" content="© 2024 SeniorThrive. All rights reserved." />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="General" />
      <meta name="distribution" content="Global" />
      
      {/* Performance and Resource Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="preconnect" href="https://cdn.jotfor.ms" />
      <link rel="dns-prefetch" href={BASE_URL} />
      
      {/* Mobile and PWA Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <meta name="theme-color" content="#3B82F6" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="SeniorThrive" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Security Headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
      
      {/* Open Graph - Enhanced */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={optimizedDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - SeniorThrive`} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SeniorThrive" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      
      {/* Article Specific Meta Tags */}
      {article && (
        <>
          {article.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags?.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card - Enhanced */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SeniorThrive" />
      <meta name="twitter:creator" content="@SeniorThrive" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={optimizedDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${title} - SeniorThrive`} />
      
      {/* Additional Social Media */}
      <meta property="fb:app_id" content="SeniorThrive" />
      <meta name="pinterest-rich-pin" content="true" />
      
      {/* Structured Data - Always Include Core Schemas */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      
      {/* Conditional Structured Data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {localBusinessSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      
      {customSchema && (
        <script type="application/ld+json">
          {JSON.stringify(customSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default UnifiedSEO;